/** R3 native course Help (#1294): one thread per learner per course with its teaching team. */
import { api } from "./client";
import { checkCommit, commitOptions, type CommitCheck } from "./commitSeams";

export type HelpReference = { kind: "session" | "work"; id: string; label: string };
export interface HelpMessage {
  id: string;
  seq: number;
  body: string;
  created_at: string;
  author: { kind: "learner" | "teacher"; name: string | null; is_me: boolean };
  reference: HelpReference | null;
  /** Learner messages only: a reply has answered it. */
  answered?: boolean;
}
export interface MyHelp {
  course_id: string;
  writable: boolean;
  audience: string;
  retention: string;
  read_only: string | null;
  /** ISO date: the thread's retention deadline, or the one a first question would capture. */
  retention_deadline: string | null;
  thread_id: string | null;
  messages: HelpMessage[];
  read_through: number;
  unread: number;
}
export interface HelpThreadRow {
  id: string;
  learner_name: string;
  unanswered: number;
  last_learner_seq: number;
  last_at: string | null;
  learner_open: boolean;
  retention_deadline: string | null;
}
export interface HelpThreads { course_id: string; threads: HelpThreadRow[]; unanswered_threads: number }
export interface HelpThread {
  course_id: string;
  thread_id: string;
  learner_name: string;
  learner_open: boolean;
  retention_deadline: string | null;
  can_reply: boolean;
  messages: HelpMessage[];
  last_learner_seq: number;
  unanswered: number;
}
export interface HelpSent { state: "confirmed"; thread_id: string; message_id: string; seq: number }
export interface AskHelp { body: string; session_id?: string | null; assignment_id?: string | null }

function sent(value: unknown): HelpSent {
  const result = value as HelpSent;
  if (!result || result.state !== "confirmed" || !result.thread_id || !result.message_id || typeof result.seq !== "number")
    throw new TypeError("Invalid Help receipt");
  return result;
}

const base = (courseId: string) => `/courses/${encodeURIComponent(courseId)}/help`;

export const courseHelpApi = {
  mine: (courseId: string) => api.get<MyHelp>(base(courseId)),
  ask: async (courseId: string, body: AskHelp, key: string) => sent(await api.post<unknown>(`${base(courseId)}/messages`, body, commitOptions(key))),
  markRead: (courseId: string, readThrough: number) => api.post<{ read_through: number }>(`${base(courseId)}/read`, { read_through: readThrough }),
  threads: (courseId: string) => api.get<HelpThreads>(`${base(courseId)}/threads`),
  thread: (courseId: string, threadId: string) => api.get<HelpThread>(`${base(courseId)}/threads/${encodeURIComponent(threadId)}`),
  reply: async (courseId: string, threadId: string, body: { body: string; answers_through: number }, key: string) =>
    sent(await api.post<unknown>(`${base(courseId)}/threads/${encodeURIComponent(threadId)}/replies`, body, commitOptions(key))),
  /** Reads the receipt under the same key; it never sends. */
  check: async (key: string, action: "course-help.ask" | "course-help.reply", subjects: string[]): Promise<CommitCheck<HelpSent>> => {
    const answer = await checkCommit(key, sent);
    if ("receipt" in answer && (answer.receipt.action !== action || answer.receipt.subject_ids.join("/") !== subjects.join("/")))
      throw new TypeError("Check command changed");
    return answer;
  },
};

export const courseHelpKey = (tenant: string | undefined, user: string | undefined, courseId: string, ...rest: string[]) =>
  ["course-help", tenant, user, courseId, ...rest] as const;
